// components/TextInput.tsx
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Textarea } from '../ui/textarea';

type Props = {
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  helperText?: string;
} & Omit<GeneralFormProps, 'description'>;

function RHFShadcnTextarea({ ref, name, title, helperText, placeholder, required, value: externalValue, onChange: externalOnChange, rows, maxLength, ...props }: Props & { ref?: React.RefObject<HTMLTextAreaElement | null> }) {
  const translate = useTranslations();
  const { control } = useFormContext<Record<string, string>>();

  const hasTitle = title !== undefined && title !== '';
  function buildFloatingLabel(): string | undefined {
    if (hasTitle === false) {
      return undefined;
    }
    if (required === true) {
      return `${title} *`;
    }
    return title;
  }
  const floatingLabel = buildFloatingLabel();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // Destructure field properties to avoid passing incompatible types
        const { value, onChange, onBlur, name: fieldName, ref: fieldRef } = field;

        // Remove defaultValue from props if it exists
        const { defaultValue, ...restProps } = props;

        const stringValue = String(value ?? externalValue ?? '');
        const currentLength = stringValue.length;
        const showCounter
          = maxLength !== undefined && currentLength >= Math.floor(maxLength * 0.8);

        return (
          <FormItem className="w-full">
            <FormControl>
              <Textarea
                name={fieldName}
                ref={ref || fieldRef}
                onBlur={onBlur}
                rows={rows !== undefined ? rows : undefined}
                required={required === true ? required : undefined}
                variant={fieldState.error !== undefined ? 'error' : 'default'}
                data-testid={fieldName}
                label={floatingLabel}
                placeholder={placeholder}
                maxLength={maxLength}
                className="resize-none"
                onChange={(e) => {
                  if (externalOnChange !== undefined) {
                    externalOnChange(e);
                    return;
                  }
                  onChange(e);
                }}
                value={value ?? externalValue ?? ''}
                {...restProps}
              />
            </FormControl>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {helperText !== undefined && helperText !== '' && fieldState.error === undefined && (
                  <FormDescription className="mt-1 type-body-s text-on-surface-variant">
                    {helperText}
                  </FormDescription>
                )}
                <FormMessage className="mt-1.5" />
              </div>
              {showCounter === true && maxLength !== undefined && (
                <span
                  className="type-body-s tabular-nums text-on-surface-variant shrink-0"
                  aria-hidden="true"
                >
                  {translate('form.charactersUsed', {
                    count: currentLength,
                    max: maxLength,
                  })}
                </span>
              )}
            </div>
          </FormItem>
        );
      }}
    />
  );
}

RHFShadcnTextarea.displayName = 'RHFShadcnTextarea';

export default RHFShadcnTextarea;
